import { type ReactNode, useState } from "react";
import { Badge, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import NoImage from "../../../../assets/images/utility/no_image.png";
import { INVENTORY_STATES, SALE_STATES } from "../../../../components/core/blocks/AdPlan/constants";
import { isValidDataframeDate } from "../../../../utils/formatText";
import ExternalProductLink from "../../../../components/custom/merchandise/ExternalProductLink";
import TitleLink from "../../../../components/core/basic/TitleLink";
import TrellisLink from "../../../../components/core/basic/TrellisLink";
import { getURLPrefix } from "../../../../utils/getUrlPrefix";
import moment from "moment";
import BuyBoxOffers from "../../../pricingv2/cards/BuyBoxOffers";
import { trellisPalette } from "../../../../components/custom/analytics/palettes";
import { Button } from "../../../../components/core/basic/Button";
import { css, cx } from "../../../../../styled-system/css";
import { OverflowTags } from "../../../../components/core/blocks/OverflowTags";
import type { TagsEnhancedItem } from "../../../../zodios/common/tagsSchema";

const INVENTORY_STATE_KEY: Record<string, "Overstock" | "Low Stock" | "Out of Stock" | "In Stock"> = {
  OVERSTOCK: "Overstock",
  LOW_STOCK: "Low Stock",
  IN_STOCK_SCARCE: "Low Stock",
  OUT_OF_STOCK: "Out of Stock",
  IN_STOCK: "In Stock",
};

const SALE_STATE_KEY: Record<string, "Best Seller" | "Clearance" | "Growth" | "New Product" | "Inactive"> = {
  BEST_SELLER: "Best Seller",
  CLEARANCE: "Clearance",
  NORMAL: "Growth",
  NEW_PRODUCT: "New Product",
  INACTIVE: "Inactive",
};

const PRICING_STATUS_KEY: Record<string, "Enrolled" | "Paused" | "Not Enrolled"> = {
  enabled: "Enrolled",
  disabled: "Paused",
  not_enrolled: "Not Enrolled",
};

const URL_PREFIX = getURLPrefix();

interface ProductCellProps {
  value: string;
  date?: string;
  showInventoryBadge?: boolean;
  showOffers?: boolean;
  to?: string;
  original: {
    product_id: string;
    sales_state: string;
    stock_status: string;
    inventory_state: string;
    price_override_end?: string;
    pricing_plan__status?: "not_enrolled" | "enabled" | "disabled" | "" | null;
    pricing_plan__id?: number;
    asin: string;
    sku: string;
    tags?: string[];
    tags_enhanced?: TagsEnhancedItem[];
    primary_variant?: boolean | null;
    cellContext: string;
  };
  className?: string;
  linkToNewTab?: boolean;
}

export const ProductCell = (props: ProductCellProps) => {
  const { t } = useTranslation(["merchandising", "common"]);
  const [viewOffers, setViewOffers] = useState(false);
  let {
    product_id: productId,
    sales_state: salesState,
    stock_status: inventoryState,
    inventory_state,
    price_override_end: priceOverrideEnd,
    pricing_plan__status,
    cellContext,
    tags,
    tags_enhanced,
    primary_variant: primaryVariant,
  } = props.original;

  // TODO: Clarify how we want to handle stock status vs. inventory state
  // Right now, user-selected inventory state takes precedence in filters, stock status from Amazon a fallback
  if (inventoryState === "IN_STOCK") {
    inventoryState = props.original.stock_status;
  }

  if (inventoryState === "IN_STOCK_SCARCE") {
    inventoryState = "LOW_STOCK";
  }
  if (inventory_state === "OVERSTOCK") {
    inventoryState = "OVERSTOCK";
  }

  const image = `${URL_PREFIX}/api/productimage/?product=${productId}`;

  return (
    <>
      <div
        className={cx(
          css({ display: "flex", fontSize: "1.5rem", fontWeight: "500", textAlign: "left" }),
          props.className,
        )}
      >
        <div
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            flexShrink: 0,
            width: 50,
            height: 50,
            marginRight: "10px",
            background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain`,
          }}
          draggable="false"
        />
        <div className="overflow-ellipsis d-flex flex-column">
          <DashboardLink product={productId} linkToNewTab={props.linkToNewTab} to={props.to}>
            {props.value}
          </DashboardLink>
          <div>
            <i
              className="pe-3"
              style={{
                fontWeight: "light",
                color: "#666",
                fontSize: "1rem",
              }}
            >
              {props.original.asin} ({props.original.sku})
            </i>
            <ExternalProductLink asin={props.original.asin} />
            {primaryVariant ?
              <Badge
                className="badge bg-trellis-fuchsia"
                pill
                style={{
                  borderRadius: "5px",
                  marginLeft: "1rem",
                }}
              >
                {t(($) => $.merchandising["Primary Variant"])}
              </Badge>
            : null}
            {(inventoryState !== "IN_STOCK" || props.showInventoryBadge) && INVENTORY_STATES[inventoryState] && (
              <Badge
                className="badge bg-trellis-purple"
                pill
                style={{
                  borderRadius: "5px",
                  marginLeft: "1rem",
                }}
              >
                {t(($) => $.merchandising.inventoryState[INVENTORY_STATE_KEY[inventoryState]!])}
              </Badge>
            )}
            {salesState !== "NORMAL" && SALE_STATES[salesState] && (
              <Badge
                className="badge bg-trellis-fuchsia"
                pill
                style={{
                  borderRadius: "5px",
                  marginLeft: "1rem",
                }}
              >
                {t(($) => $.merchandising.saleState[SALE_STATE_KEY[salesState]!])}
              </Badge>
            )}
            {(
              pricing_plan__status &&
              pricing_plan__status !== "not_enrolled" &&
              cellContext !== "advertising" &&
              cellContext !== "content"
            ) ?
              <Badge
                className={`${pricing_plan__status === "enabled" ? "bg-trellis-green" : "bg-trellis-yellow"}`}
                pill
                style={{
                  borderRadius: "5px",
                  marginLeft: "1rem",
                }}
              >
                {t(($) => $.merchandising.pricingStatus[PRICING_STATUS_KEY[pricing_plan__status]!])}
              </Badge>
            : ""}
            {(
              isValidDataframeDate(priceOverrideEnd) &&
              moment().isBefore(priceOverrideEnd) &&
              cellContext !== "advertising" &&
              cellContext !== "content"
            ) ?
              <Badge
                className="badge bg-trellis-purple"
                pill
                style={{
                  borderRadius: "5px",
                  marginLeft: "1rem",
                }}
              >
                {t(($) => $.merchandising["Fixed Price"])}
              </Badge>
            : ""}

            <OverflowTags enhancedTags={tags_enhanced} tags={tags} id={productId} />

            {props.showOffers && (
              <span
                onClick={() => {
                  setViewOffers(true);
                }}
                className="fs-5 ms-3"
                style={{ cursor: "pointer", color: trellisPalette[0] }}
              >
                {t(($) => $.merchandising["View Offers"])}
              </span>
            )}
          </div>
        </div>
      </div>
      <Modal
        show={viewOffers}
        onHide={() => {
          setViewOffers(false);
        }}
        animation={false}
        size={"xl"}
      >
        <Modal.Body>
          <BuyBoxOffers asin={props.original.asin} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            className={css({ margin: "1rem" })}
            onPress={() => {
              setViewOffers(false);
            }}
          >
            {t(($) => $.common.actions.Close)}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

type DashboardLinkProps =
  | {
      children: string;
      category?: string;
      product: string;
      linkToNewTab?: boolean;
      to?: string;
    }
  | {
      product?: never;
      category?: string;
      children: ReactNode;
      linkToNewTab?: boolean;
      to?: string;
    };

export const DashboardLink = ({
  children,
  category,
  product,
  linkToNewTab = false,
  to: toProp,
}: DashboardLinkProps) => {
  const to =
    toProp ??
    getDashboardTo({
      resourceId: category || product,
      type: category ? DASHBOARD_TYPES.CATEGORY : DASHBOARD_TYPES.PRODUCT,
    });

  if (product) {
    return <TitleLink to={to} target={linkToNewTab ? "_blank" : ""} title={children} />;
  }
  return (
    <TrellisLink
      to={to}
      style={{
        textDecoration: "none",
        fontWeight: "500",
        fontSize: "1.5rem",
        cursor: "pointer",
      }}
    >
      {children}
    </TrellisLink>
  );
}; // TODO factor this out into a utils file

// oxlint-disable-next-line eslint/no-redeclare -- baseline on new rule introduction
export const DASHBOARD_TYPES = {
  PRODUCT: "product",
  CATEGORY: "category",
  ROOT: "root",
} as const;
export type DASHBOARD_TYPES = (typeof DASHBOARD_TYPES)[keyof typeof DASHBOARD_TYPES];

export const getDashboardTo = ({ resourceId, type }: { resourceId?: string; type?: DASHBOARD_TYPES }) => {
  let to = `/user/dashboard`;
  if (resourceId) {
    switch (type) {
      case DASHBOARD_TYPES.CATEGORY:
        to = `/user/merchandising/category/${resourceId}`;
        break;
      case DASHBOARD_TYPES.PRODUCT:
        to = `/user/merchandising/product/${resourceId}`;
        break;
      default:
        to = `/user/dashboard`;
    }
  }
  return to;
};
